import { AppStoreConstructorArgs, AppSubStore, AppSubStoreArgs } from "@/store/types";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { AppStoreQueriesCacheStore } from "@/store/queries/AppStoreQueriesCacheStore";
import { AppStoreSpaceStore } from "@/store/space/AppStoreSpaceStore";
import { AppStoreSpaceAccountStore } from "@/store/space-account/AppStoreSpaceAccountStore";
import { AppStoreRoutingStore } from "@/store/routing/AppStoreRoutingStore";
import { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";
import { AppStoreAccountStore } from "@/store/account/AppStoreAccountStore";
import pRetry from "p-retry";
import { initializeAccountObservability } from "@/store/utils/initialization";
import { BaseError, StoreError } from "@/domains/errors";
import { PublicAppStore } from "@/store/PublicAppStore";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import localDb from "@/domains/local-db";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { AppStorePlatformInfoStore } from "@/store/platform-info/AppStorePlatformInfoStore";
import { AppStoreSyncStore } from "@/store/sync/AppStoreSyncStore";
import { AppStoreNoteStore } from "@/store/note/AppStoreNoteStore";
import { AppStoreCollectionsStore } from "@/store/collections/AppStoreCollectionsStore";
import { AppStoreCollectionItemsStore } from "@/store/collection-items/AppStoreCollectionItemsStore";
import { AppStoreFavoriteItemsStore } from "@/store/favorite-items/AppStoreFavoriteItemsStore";
import { AppStoreFeedbackStore } from "@/store/feedback/AppStoreFeedbackStore";
import { AppStoreSavedSearchesStore } from "@/store/saved-searches/AppStoreSavedSearchesStore";
import { AppStoreSpaceAccountNotesStore } from "@/store/recent-items/AppStoreSpaceAccountNotesStore";
import { AppStoreSpaceAccountCollectionsStore } from "@/store/recent-items/AppStoreSpaceAccountCollectionsStore";
import { AppStoreRecentItemsStore } from "@/store/recent-items/AppStoreRecentItemsStore";
import { AppStoreContactsStore } from "@/store/contacts/AppStoreContactsStore";
import { AppStoreCollectionMetadataStore } from "@/store/collections/AppStoreCollectionMetadataStore";
import { AppStoreSpaceAccountContactsStore } from "@/store/contacts/AppStoreSpaceAccountContactsStore";
import { ModalsStore } from "@/store/modals/ModalsStore";
import { ProactiveLostAccessModalStore } from "@/store/modals/ProactiveLostAccessModalStore";
import { AppStoreChatConversationStore } from "@/store/chat/ChatConversationStore";
import { AppStoreChatMessageStore } from "@/store/chat/ChatMessageStore";
import { AppStoreSpaceAccountChatMessagesStore } from "@/store/chat/AppStoreSpaceAccountChatMessagesStore";
import { AppStoreNoteContentDocumentStore } from "@/store/note/AppStoreNoteContentDocumentStore";
import { AppStoreSidePanelStore } from "@/store/routing/AppStoreSidePanelStore";
import { AppStoreSpaceAccountFeatureFlagsStore } from "./feature-flags/AppStoreSpaceAccountFeatureFlagsStore";
import { windowModule } from "@/modules/window";
import { MemDB } from "@/domains/dexie-db";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import { Search } from "@/domains/search";
import { AppStoreDataImportsStore } from "@/store/data-imports/AppStoreDataImportsStore";
import { AppStoreDataExportsStore } from "@/store/data-exports/AppStoreDataExportsStore";
import { AppStoreSpaceAccountAppCalloutsStore } from "@/store/app-callout/AppStoreSpaceAccountAppCalloutsStore";
import { AppStoreApiKeysStore } from "@/store/api-keys/AppStoreApiKeysStore";
import { AppStoreSpaceAccountTemplateStore } from "@/store/templates/AppStoreSpaceAccountTemplateStore";
import { AppStoreTemplateContentDocumentStore } from "@/store/templates/AppStoreTemplateContentDocumentStore";
import { AppStoreTemplateStore } from "@/store/templates/AppStoreTemplateStore";
import { LogoutState } from "@/components/sync/types";
import { TabLifecycleManager, TabLifecycleState } from "@/store/tab-lifecycle";
import { TabDetector } from "@/store/tab-detector";
import { AppStoreSessionStore } from "@/store/sessions/AppStoreSessionStore";
import { AppStoreSessionEventStore } from "@/store/sessions/AppStoreSessionEventStore";
import { AppStoreNoteSourceStore } from "@/store/sources/AppStoreNoteSourceStore";
import { AppStoreUploadedFileBatchStore } from "@/store/uploaded-files/AppStoreUploadedFileBatchStore";
import { AppStoreUploadedFileStore } from "@/store/uploaded-files/AppStoreUploadedFileStore";
import { AppStoreSourceStore } from "@/store/sources/AppStoreSourceStore";
import { AppStoreUploadProgressStore } from "@/store/upload-progress/AppStoreUploadProgressStore";
import { AppStoreEmailSendersStore } from "@/store/email-senders/AppStoreEmailSendersStore";
import { IEmailSenderStore } from "@/store/email-senders/types";
import { ITextMessageSenderStore } from "@/store/text-message-senders/types";
import { AppStoreTextMessageSendersStore } from "@/store/text-message-senders/AppStoreTextMessageSendersStore";
import { AppStoreZapierIntegrationStore } from "@/store/zapier-integration/AppStoreZapierIntegrationStore";
import { IZapierIntegrationStore } from "@/store/zapier-integration/types";
import { AppStoreAudioRecordingStore } from "@/store/audio-recordings";
import { AppStoreSubscriptionStore } from "@/store/subscriptions/AppStoreSubscriptionStore";
import { IChromeExtensionStore } from "@/store/chrome-extension/types";
import { AppStoreChromeExtensionStore } from "@/store/chrome-extension/AppStoreChromeExtensionStore";
import { AppStoreFeatureGatesStore } from "@/store/feature-gates/AppStoreFeatureGatesStore";
import { AppStoreHeadsUpStore } from "@/store/heads-up/AppStoreHeadsUpStore";
import { DatabaseManager } from "@/domains/tanstack-db";
import { AppStoreLayoutStore } from "@/store/layout/AppStoreLayoutStore";
import type { PageStore } from "@/store/PageStore";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import { AppStoreDexieStore } from "@/store/repositories/AppStoreDexieStore";
import { identifyUserInMetrics, MetricsBrowserMetadata } from "@/domains/metrics";
import { AppStoreMetricsStore } from "@/store/metrics/AppStoreMetricsStore";
import { AppStoreAudioStore } from "@/store/audio/audio.store";
import { AppStoreCalendarStore } from "@/store/calendar/calendar.store";
import { CacheStore } from "@/modules/cache";
import { getApiAuthIdentifier } from "@/modules/api/authUtils";
import { ACCOUNT_CACHE_DB_IDENTIFIER } from "@/modules/cache/constants";
import { AppStoreGettingStartedStore } from "@/store/getting-started/AppStoreGettingStartedStore";
import { AppStoreSpaceAccountCalendarStore } from "@/store/calendar/space-account-calendar/AppStoreSpaceAccountCalendarStore";
import { AppStoreSpaceAccountCalendarConnectionStore } from "@/store/calendar/space-account-calendar-connection/AppStoreSpaceAccountCalendarConnectionStore";
import { AppStoreSpaceAccountCalendarEventStore } from "@/store/calendar/space-account-calendar-event/AppStoreSpaceAccountCalendarEventStore";
import { AppStoreSpaceAccountSettingsStore } from "@/store/space-account-settings/AppStoreSpaceAccountSettingsStore";
import { AppStoreSpaceAccountNoteAttachmentsStore } from "@/store/note-attachments/AppStoreSpaceAccountNoteAttachmentsStore";
import { AppStoreOneClickImportStore } from "@/store/one-click-import/AppStoreOneClickImportStore";
import { OnboardingStore } from "@/store/onboarding/OnboardingStore";
import { CalendarOnboardingStore } from "@/store/onboarding/CalendarOnboardingStore";
import { SettingsModalStore } from "@/store/settings-modal/SettingsModalStore";
import { SettingsModalTemplatesPageStore } from "@/store/settings-modal/SettingsModalTemplatesPageStore";
import { AppStoreMemAgentStore } from "@/store/mem-agent/mem-agent-store";
import { AgentOverlayStore } from "@/store/agent-overlay/agent-overlay-store";
import {
  AppStoreAssistantFollowUpsStore,
  AppStoreAssistantProjectsStore,
  AppStoreAssistantSyncModelStore,
  AppStoreAssistantTasksStore,
  AssistantConversationItemModelData,
  AssistantConversationModelData,
  AssistantFollowUpChangeRequestModelData,
  AssistantNoteActivityModelData,
  AssistantNoteReviewStatusModelData,
  AssistantObjectiveModelData,
  AssistantProjectActivityModelData,
  AssistantProjectChangeRequestModelData,
  AssistantProjectLabelAssignmentModelData,
  AssistantProjectLabelGroupModelData,
  AssistantProjectLabelModelData,
  AssistantRoutineModelData,
  AssistantSpaceAccountSettingsModelData,
  AssistantTaskActivityModelData,
  AssistantTaskChangeRequestModelData,
  AssistantTaskLabelAssignmentModelData,
  AssistantTaskLabelGroupModelData,
  AssistantTaskLabelModelData,
  assistantSyncModelIndexers,
} from "@/store/assistant";
import { SyncModelKind } from "@/store/sync/types";

export class AppStore {
  db: DatabaseManager;
  apiKeys: AppStoreApiKeysStore;
  account: AppStoreAccountStore;
  accountCache: CacheStore;
  audio: AppStoreAudioStore;
  audioRecordings: AppStoreAudioRecordingStore;
  calendar: AppStoreCalendarStore;
  calendarOnboarding: CalendarOnboardingStore;
  chatConversations: AppStoreChatConversationStore;
  chatMessages: AppStoreChatMessageStore;
  collectionItems: AppStoreCollectionItemsStore;
  collectionMetadata: AppStoreCollectionMetadataStore;
  collections: AppStoreCollectionsStore;
  contacts: AppStoreContactsStore;
  dataImports: AppStoreDataImportsStore;
  dataExports: AppStoreDataExportsStore;
  favoriteItems: AppStoreFavoriteItemsStore;
  feedback: AppStoreFeedbackStore;
  modals: ModalsStore;
  navigation: AppStoreNavigationStore;
  notes: AppStoreNoteStore;
  noteContentDocuments: AppStoreNoteContentDocumentStore;
  oneClickImport: AppStoreOneClickImportStore;
  platformInfo: AppStorePlatformInfoStore;
  onboarding: OnboardingStore;

  proactiveLostAccessModal: ProactiveLostAccessModalStore;
  publicAppStore: PublicAppStore;
  queriesCache: AppStoreQueriesCacheStore;
  recentItems: AppStoreRecentItemsStore;
  routing: AppStoreRoutingStore;
  savedSearches: AppStoreSavedSearchesStore;
  sidePanel: AppStoreSidePanelStore;
  spaceAccountAppCallouts: AppStoreSpaceAccountAppCalloutsStore;
  spaceAccountChatMessages: AppStoreSpaceAccountChatMessagesStore;
  spaceAccountCollections: AppStoreSpaceAccountCollectionsStore;
  spaceAccountContacts: AppStoreSpaceAccountContactsStore;
  spaceAccountNotes: AppStoreSpaceAccountNotesStore;
  spaceAccountFeatureFlags: AppStoreSpaceAccountFeatureFlagsStore;
  spaceAccountTemplates: AppStoreSpaceAccountTemplateStore;
  spaceAccountCalendars: AppStoreSpaceAccountCalendarStore;
  spaceAccountCalendarConnections: AppStoreSpaceAccountCalendarConnectionStore;
  spaceAccountCalendarEvents: AppStoreSpaceAccountCalendarEventStore;
  spaceAccountSettings: AppStoreSpaceAccountSettingsStore;
  spaceAccountNoteAttachments: AppStoreSpaceAccountNoteAttachmentsStore;
  spaceAccounts: AppStoreSpaceAccountStore;
  spaces: AppStoreSpaceStore;
  sync: AppStoreSyncStore;
  templates: AppStoreTemplateStore;
  templateContentDocuments: AppStoreTemplateContentDocumentStore;
  sessions: AppStoreSessionStore;
  sessionEvents: AppStoreSessionEventStore;
  sources: AppStoreSourceStore;
  noteSources: AppStoreNoteSourceStore;
  uploadedFiles: AppStoreUploadedFileStore;
  uploadedFileBatches: AppStoreUploadedFileBatchStore;
  uploadProgress: AppStoreUploadProgressStore;
  subscriptions: AppStoreSubscriptionStore;
  featureGates: AppStoreFeatureGatesStore;
  headsUp: AppStoreHeadsUpStore;
  layout: AppStoreLayoutStore;
  pageStore?: PageStore;
  dexie: AppStoreDexieStore;
  metrics: AppStoreMetricsStore;
  gettingStarted: AppStoreGettingStartedStore;
  settingsModal: SettingsModalStore;
  settingsModalTemplatesPage: SettingsModalTemplatesPageStore;
  agentOverlay: AgentOverlayStore;
  memAgent: AppStoreMemAgentStore;
  assistantTasks: AppStoreAssistantTasksStore;
  assistantProjects: AppStoreAssistantProjectsStore;
  assistantFollowUps: AppStoreAssistantFollowUpsStore;
  assistantTaskLabelGroups: AppStoreAssistantSyncModelStore<AssistantTaskLabelGroupModelData>;
  assistantTaskLabels: AppStoreAssistantSyncModelStore<AssistantTaskLabelModelData>;
  assistantTaskLabelAssignments: AppStoreAssistantSyncModelStore<AssistantTaskLabelAssignmentModelData>;
  assistantProjectLabelGroups: AppStoreAssistantSyncModelStore<AssistantProjectLabelGroupModelData>;
  assistantProjectLabels: AppStoreAssistantSyncModelStore<AssistantProjectLabelModelData>;
  assistantProjectLabelAssignments: AppStoreAssistantSyncModelStore<AssistantProjectLabelAssignmentModelData>;
  assistantTaskActivities: AppStoreAssistantSyncModelStore<AssistantTaskActivityModelData>;
  assistantProjectActivities: AppStoreAssistantSyncModelStore<AssistantProjectActivityModelData>;
  assistantNoteActivities: AppStoreAssistantSyncModelStore<AssistantNoteActivityModelData>;
  assistantRoutines: AppStoreAssistantSyncModelStore<AssistantRoutineModelData>;
  assistantObjectives: AppStoreAssistantSyncModelStore<AssistantObjectiveModelData>;
  assistantNoteReviewStatuses: AppStoreAssistantSyncModelStore<AssistantNoteReviewStatusModelData>;
  assistantSpaceAccountSettings: AppStoreAssistantSyncModelStore<AssistantSpaceAccountSettingsModelData>;
  assistantConversations: AppStoreAssistantSyncModelStore<AssistantConversationModelData>;
  assistantConversationItems: AppStoreAssistantSyncModelStore<AssistantConversationItemModelData>;
  assistantTaskChangeRequests: AppStoreAssistantSyncModelStore<AssistantTaskChangeRequestModelData>;
  assistantProjectChangeRequests: AppStoreAssistantSyncModelStore<AssistantProjectChangeRequestModelData>;
  assistantFollowUpChangeRequests: AppStoreAssistantSyncModelStore<AssistantFollowUpChangeRequestModelData>;

  accountLoadingState: AsyncResult<boolean> = asyncResultModule.setLoading();
  mountedState: AsyncResult<boolean> = asyncResultModule.setLoading();
  search: Search;
  tabLifecycleManager: TabLifecycleManager;
  tabDetector: TabDetector;
  emailSenders: IEmailSenderStore;
  chromeExtension: IChromeExtensionStore;
  textMessageSenders: ITextMessageSenderStore;
  zapierIntegration: IZapierIntegrationStore;

  protected _memDb: MemDB | undefined;
  private _readyForMetricsInitialization: boolean = false;
  private _metricsBrowserMetadata: MetricsBrowserMetadata | null = null;
  private _metricsBrowserMetadataUnsubscribe: (() => void) | null = null;

  constructor({ publicAppStore, api, pusher, database }: AppStoreConstructorArgs) {
    const injectedDeps: AppSubStoreArgs = { store: this, api, pusher };

    this.publicAppStore = publicAppStore;
    this.db = database ?? new DatabaseManager();
    this.tabLifecycleManager = new TabLifecycleManager();
    this.tabDetector = new TabDetector();
    this.apiKeys = new AppStoreApiKeysStore(injectedDeps);
    this.account = new AppStoreAccountStore(injectedDeps);
    this.accountCache = new CacheStore({
      databaseId: ACCOUNT_CACHE_DB_IDENTIFIER,
      scopeResolver: getApiAuthIdentifier,
    });
    this.dexie = new AppStoreDexieStore(injectedDeps);

    // These need to be initialized before any other stores that depend on them.
    this.modals = new ModalsStore(injectedDeps);
    this.navigation = new AppStoreNavigationStore(injectedDeps);

    this.audio = new AppStoreAudioStore(injectedDeps);
    this.calendar = new AppStoreCalendarStore(injectedDeps);
    this.audioRecordings = new AppStoreAudioRecordingStore(injectedDeps);
    this.chatConversations = new AppStoreChatConversationStore(injectedDeps);
    this.chatMessages = new AppStoreChatMessageStore(injectedDeps);
    this.collectionItems = new AppStoreCollectionItemsStore(injectedDeps);
    this.collectionMetadata = new AppStoreCollectionMetadataStore(injectedDeps);
    this.collections = new AppStoreCollectionsStore(injectedDeps);
    this.contacts = new AppStoreContactsStore(injectedDeps);
    this.dataImports = new AppStoreDataImportsStore(injectedDeps);
    this.dataExports = new AppStoreDataExportsStore(injectedDeps);
    this.favoriteItems = new AppStoreFavoriteItemsStore(injectedDeps);
    this.feedback = new AppStoreFeedbackStore(injectedDeps);
    this.notes = new AppStoreNoteStore(injectedDeps);
    this.noteContentDocuments = new AppStoreNoteContentDocumentStore(injectedDeps);
    this.oneClickImport = new AppStoreOneClickImportStore(injectedDeps);
    this.platformInfo = new AppStorePlatformInfoStore(injectedDeps);
    this.calendarOnboarding = new CalendarOnboardingStore(injectedDeps);
    this.onboarding = new OnboardingStore(injectedDeps);

    this.queriesCache = new AppStoreQueriesCacheStore();
    this.recentItems = new AppStoreRecentItemsStore(injectedDeps);
    this.routing = new AppStoreRoutingStore(injectedDeps);
    this.savedSearches = new AppStoreSavedSearchesStore(injectedDeps);
    this.spaceAccountAppCallouts = new AppStoreSpaceAccountAppCalloutsStore(injectedDeps);
    this.spaceAccountChatMessages = new AppStoreSpaceAccountChatMessagesStore(injectedDeps);
    this.spaceAccountCollections = new AppStoreSpaceAccountCollectionsStore(injectedDeps);
    this.spaceAccountContacts = new AppStoreSpaceAccountContactsStore(injectedDeps);
    this.spaceAccountNotes = new AppStoreSpaceAccountNotesStore(injectedDeps);
    this.spaceAccountFeatureFlags = new AppStoreSpaceAccountFeatureFlagsStore(injectedDeps);
    this.spaceAccountTemplates = new AppStoreSpaceAccountTemplateStore(injectedDeps);
    this.spaceAccountCalendars = new AppStoreSpaceAccountCalendarStore(injectedDeps);
    this.spaceAccountCalendarConnections = new AppStoreSpaceAccountCalendarConnectionStore(
      injectedDeps
    );
    this.spaceAccountCalendarEvents = new AppStoreSpaceAccountCalendarEventStore(injectedDeps);
    this.spaceAccountSettings = new AppStoreSpaceAccountSettingsStore(injectedDeps);
    this.spaceAccountNoteAttachments = new AppStoreSpaceAccountNoteAttachmentsStore(injectedDeps);
    this.spaceAccounts = new AppStoreSpaceAccountStore(injectedDeps);
    this.spaces = new AppStoreSpaceStore(injectedDeps);
    this.search = new Search({ store: this });
    this.templates = new AppStoreTemplateStore(injectedDeps);
    this.templateContentDocuments = new AppStoreTemplateContentDocumentStore(injectedDeps);
    this.sessions = new AppStoreSessionStore(injectedDeps);
    this.sessionEvents = new AppStoreSessionEventStore(injectedDeps);
    this.sources = new AppStoreSourceStore(injectedDeps);
    this.noteSources = new AppStoreNoteSourceStore(injectedDeps);
    this.uploadedFileBatches = new AppStoreUploadedFileBatchStore(injectedDeps);
    this.uploadedFiles = new AppStoreUploadedFileStore(injectedDeps);
    this.uploadProgress = new AppStoreUploadProgressStore(injectedDeps);
    this.subscriptions = new AppStoreSubscriptionStore(injectedDeps);
    this.featureGates = new AppStoreFeatureGatesStore(injectedDeps);
    this.headsUp = new AppStoreHeadsUpStore(injectedDeps);
    this.layout = new AppStoreLayoutStore(injectedDeps);
    this.metrics = new AppStoreMetricsStore(injectedDeps);
    this.gettingStarted = new AppStoreGettingStartedStore(injectedDeps);
    this.settingsModal = new SettingsModalStore(injectedDeps);
    this.settingsModalTemplatesPage = new SettingsModalTemplatesPageStore(injectedDeps);
    this.agentOverlay = new AgentOverlayStore(injectedDeps);
    this.memAgent = new AppStoreMemAgentStore(this);
    this.assistantTasks = new AppStoreAssistantTasksStore(injectedDeps);
    this.assistantProjects = new AppStoreAssistantProjectsStore(injectedDeps);
    this.assistantFollowUps = new AppStoreAssistantFollowUpsStore(injectedDeps);
    this.assistantTaskLabelGroups = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskLabelGroup,
      computeModelIndexes: assistantSyncModelIndexers.taskLabelGroup,
      ...injectedDeps,
    });
    this.assistantTaskLabels = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskLabel,
      computeModelIndexes: assistantSyncModelIndexers.taskLabel,
      ...injectedDeps,
    });
    this.assistantTaskLabelAssignments = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskLabelAssignment,
      computeModelIndexes: assistantSyncModelIndexers.taskLabelAssignment,
      ...injectedDeps,
    });
    this.assistantProjectLabelGroups = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectLabelGroup,
      computeModelIndexes: assistantSyncModelIndexers.projectLabelGroup,
      ...injectedDeps,
    });
    this.assistantProjectLabels = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectLabel,
      computeModelIndexes: assistantSyncModelIndexers.projectLabel,
      ...injectedDeps,
    });
    this.assistantProjectLabelAssignments = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectLabelAssignment,
      computeModelIndexes: assistantSyncModelIndexers.projectLabelAssignment,
      ...injectedDeps,
    });
    this.assistantTaskActivities = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskActivity,
      computeModelIndexes: assistantSyncModelIndexers.taskActivity,
      ...injectedDeps,
    });
    this.assistantProjectActivities = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectActivity,
      computeModelIndexes: assistantSyncModelIndexers.projectActivity,
      ...injectedDeps,
    });
    this.assistantNoteActivities = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantNoteActivity,
      computeModelIndexes: assistantSyncModelIndexers.noteActivity,
      ...injectedDeps,
    });
    this.assistantRoutines = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantRoutine,
      computeModelIndexes: assistantSyncModelIndexers.routine,
      ...injectedDeps,
    });
    this.assistantObjectives = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantObjective,
      computeModelIndexes: assistantSyncModelIndexers.objective,
      ...injectedDeps,
    });
    this.assistantNoteReviewStatuses = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantNoteReviewStatus,
      computeModelIndexes: assistantSyncModelIndexers.noteReviewStatus,
      ...injectedDeps,
    });
    this.assistantSpaceAccountSettings = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantSpaceAccountSettings,
      computeModelIndexes: assistantSyncModelIndexers.spaceAccountSettings,
      ...injectedDeps,
    });
    this.assistantConversations = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantConversation,
      computeModelIndexes: assistantSyncModelIndexers.conversation,
      ...injectedDeps,
    });
    this.assistantConversationItems = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantConversationItem,
      computeModelIndexes: assistantSyncModelIndexers.conversationItem,
      ...injectedDeps,
    });
    this.assistantTaskChangeRequests = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantTaskChangeRequest,
      computeModelIndexes: assistantSyncModelIndexers.taskChangeRequest,
      ...injectedDeps,
    });
    this.assistantProjectChangeRequests = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantProjectChangeRequest,
      computeModelIndexes: assistantSyncModelIndexers.projectChangeRequest,
      ...injectedDeps,
    });
    this.assistantFollowUpChangeRequests = new AppStoreAssistantSyncModelStore({
      modelKind: SyncModelKind.AssistantFollowUpChangeRequest,
      computeModelIndexes: assistantSyncModelIndexers.followUpChangeRequest,
      ...injectedDeps,
    });

    // This needs to happen after every store that has a ShareSheetModalStore (Note, Template, Collection).
    this.sidePanel = new AppStoreSidePanelStore(injectedDeps);

    // This needs to be initialized after all the stores are initialized
    this.sync = new AppStoreSyncStore(injectedDeps);
    makeAutoObservable(this);

    // We need this to be already observable to react to changes its observable props.
    this.proactiveLostAccessModal = new ProactiveLostAccessModalStore(injectedDeps);
    this.emailSenders = new AppStoreEmailSendersStore(this);
    this.chromeExtension = new AppStoreChromeExtensionStore(this);
    this.textMessageSenders = new AppStoreTextMessageSendersStore(this);
    this.zapierIntegration = new AppStoreZapierIntegrationStore(this);

    // Track ui_ready event when store becomes ready
    reaction(
      () => this.readyState.data,
      isReady => {
        this.metrics.trackUiReady({ isReady });
      }
    );
  }

  public setPageStore(pageStore: PageStore) {
    this.pageStore = pageStore;
  }

  get auth() {
    return this.publicAppStore.auth;
  }

  get interface() {
    return this.publicAppStore.interface;
  }

  get debug() {
    return this.publicAppStore.debug;
  }

  get electron() {
    return this.publicAppStore.electron;
  }

  get systemPermissions() {
    return this.publicAppStore.systemPermissions;
  }

  get globalCache() {
    return this.publicAppStore.globalCache;
  }

  get memDb(): MemDB {
    if (!this._memDb) {
      throw new Error("Attempt to access MemDB before initialization");
    }

    return this._memDb;
  }

  get isMemDbReady() {
    return this._memDb !== undefined;
  }

  /**
   * Fetches data which is critical to our logged-in routes:
   * - Account info for the current user
   * - Spaces for the current user
   * - SpaceAccounts for the current user
   *
   * Note that the UI will block while these promises complete.
   * (Ideally, they implement some form of caching/persistence)
   */
  async initialize() {
    try {
      this.metrics.trackAppStoreInitialize();

      runInAction(() => {
        this.accountLoadingState = asyncResultModule.setLoading();
      });

      if (!this.auth.isAuthenticated) {
        throw new StoreError({
          message: "User must be authenticated to initialize the AppStore.",
        });
      }

      const retryOptions = {
        retries: 2,
        minDelay: 1000,
        factor: 2,
      };

      const promises = [
        pRetry(() => this.account.initialize(), retryOptions),
        pRetry(() => this.spaces.initialize(), retryOptions),
        pRetry(() => this.spaceAccounts.initialize(), retryOptions),
        pRetry(() => this.platformInfo.initialize(), retryOptions),
      ];

      /**
       * If it fails even after retries, we throw.
       */
      await Promise.all(promises);
      this._initializeUserForMetricsAndLogs();

      /**
       * Trigger any side-effects related to initializing the UI client.
       * (Wiring up user info to our error-monitor, etc.)
       */
      await initializeAccountObservability({ store: this });
      await localDb.initializeUserDb();
      await this.initializeMemDB();
      await this.spaceAccountFeatureFlags.initialize();
      await this.checkAndSetDefaultLogoutState();
      await this.initializeTanstackDatabase();

      // Load getting started progress
      await this.gettingStarted.load();

      await this.calendarOnboarding.initialize();
      await this.onboarding.initialize();

      this.addPageLifecycleReactions();

      runInAction(() => {
        this.accountLoadingState = asyncResultModule.setReady(true);
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.error({
        message: "[initialize] AppStore failed to initialize.",
        info: { err: objectModule.safeErrorAsJson(err) },
      });

      runInAction(() => {
        this.accountLoadingState = asyncResultModule.setError(err);
      });

      throw err;
    }
  }

  _initializeUserForMetricsAndLogs() {
    const { myPersonalSpaceAccountId } = this.spaceAccounts;
    const isMemmer = isMemAccount(this.account.myAccount);

    const {
      profileEmailAddress: email,
      displayName: name,
      profilePhotoUrl,
      createdAt,
    } = this.account.myAccount;

    /**
     * We fire both of these off asynchronously.
     */
    if (myPersonalSpaceAccountId) {
      void electronModule.setClientMetadata({
        context: {
          space_account_id: myPersonalSpaceAccountId,
        },
      });

      void identifyUserInMetrics({
        spaceAccountId: myPersonalSpaceAccountId,
        userProperties: {
          email,
          name,
          profilePhotoUrl,
          createdAt,
        },
        config: {
          isMemmer,
        },
      });
    }
  }

  async initializeMemDB() {
    logger.info({
      message: "[SYNC][MemDB] Initializing MemDB",
    });
    // Note: we don't have to open db connection here, it will be open on first request
    this._memDb = new MemDB(this.spaceAccounts.myPersonalSpaceAccountId);

    await this._memDb.persist();

    this.addDbListeners();
  }

  async checkAndSetDefaultLogoutState() {
    // LogoutState.LoggingOut means we interrupted a logout previously.
    // In that case, the safest bet is force user to login even if they still have auth token
    // Thats because we cannot guarantee how much data has been deleted
    const currentLogoutState = await this.memDb.settings.getLogoutState();
    logger.info({
      message: "[SYNC] Checking and setting default logout state",
      info: { currentLogoutState: currentLogoutState || "undefined" },
    });
    if (currentLogoutState !== LogoutState.LoggingOut) {
      await this.memDb.settings.setLogoutState(LogoutState.Idle);
    }
  }

  /**
   * Represents when the store is ready for use by child components.
   */
  get readyState(): AsyncResult<boolean> {
    const isError =
      this.accountLoadingState.error ||
      this.mountedState.error ||
      this.spaceAccountFeatureFlags.readyState.error;
    const isReady =
      this.accountLoadingState.data &&
      this.mountedState.data &&
      this.spaceAccountFeatureFlags.readyState.data;

    return {
      called: true,
      loading: !isReady,
      error: isError,
      data: isReady,
    } as AsyncResult<boolean>;
  }

  useInitializeAppStoreEffects = () => {
    this.queriesCache.useCacheCancellation();
    this.routing.useSyncRoutingParams();
    this.navigation.useSyncNavigation();
    this.settingsModal.useSyncSettingsSearchParam();
    this.agentOverlay.useSyncAgentSearchParam();
    this.subscriptions.usePersonalSubscription();
    this.featureGates.useInitializeAppStoreEffects();
    this.audio.recording.useInitializeAudioRecordingEffects();
    this.calendar.useInitializeCalendarEffects();

    useEffectOnMount(() => {
      const asyncLogic = async () => {
        try {
          await this.sync.initialize();
          await this.sidePanel.initialize();
          await this.audio.initialize();

          /**
           * We set this in a useEffect because it should happen
           * -after- the initial render, so the `useEffect` hooks
           * from above have a chance to run first.
           */
          runInAction(() => {
            this.mountedState = asyncResultModule.setReady(true);
          });
          logger.info({ message: "[AppStore] Ready" });
        } catch (error) {
          const safeError = objectModule.safeErrorAsJson(error as Error);

          logger.error({
            message: "[AppStore] Failed to initialize",
            info: { error: safeError },
          });

          const err = new BaseError({
            message: "[AppStore.useInitializeAppStoreEffects] Failed to initialize.",
            info: {
              error: safeError,
            },
          });

          runInAction(() => {
            this.mountedState = asyncResultModule.setError(err);
          });
        }
      };

      asyncLogic();

      /**
       * We kick off any initializers on-mount.
       */
      return () => {
        if (import.meta.hot) {
          return;
        }
        /**
         * We only trigger the `unmount` side-effect if we're not hot-reloading.
         * During hot-reload, we can just keep the state as-is.
         */
        this.debug.resetState();
        this.account.resetState();
        this.notes.resetState();
        this.audio.resetState();
        this.calendar.resetState();
        this.spaces.resetState();
        this.spaceAccounts.resetState();
        this.routing.resetState();
      };
    });
  };

  public forceHardReload = async () => {
    windowModule.forceHardReload();
  };

  public forceUpgradeClient = async () => {
    if (!clientEnvModule.isDesktop()) {
      setTimeout(() => {
        this.forceHardReload();
      }, 300);
      return;
    }

    await electronModule.updateNow({ waitForUpdate: true });
  };

  public resetStorageAndReload = async () => {
    await localDb.queue.clear();
    await localDb.syncUpdates.clear();
    await localDb.sync.clear();

    await this.memDb.syncUpdates.clear();
    await this.memDb.queue.processing.clear();
    await this.memDb.queue.pending.clear();
    await this.memDb.queue.optimisticUpdates.clear();
    await this.memDb.settings.clearNoteContentPreloadingState();
    await this.memDb.settings.clearNoteContentDocumentLastHydrateCursor();
    await this.memDb.settings.clearLastSyncId();
    await this.memDb.settings.clearFeatureFlags();

    await this.dexie.repositories.localAudioRecordingBlobs.clearAll();
    await this.dexie.repositories.localAudioRecordingTranscriptTurns.clearAll();
    await this.dexie.repositories.webAudioArtifacts.clearAll();

    setTimeout(() => {
      windowModule.forceReload();
    }, 300);
  };

  public resetAndReinitializeSync = async () => {
    this.stopSync();
    this.resetSync();
    await this.sync.initialize();
    await this.initializeTanstackDatabase();
  };

  stopSync = () => {
    this.db.stopSync().catch(() => {});

    for (const value of Object.values(this)) {
      if (value instanceof AppSubStore) {
        value.stopSync();
      }
    }
  };

  resetSync = () => {
    for (const value of Object.values(this)) {
      if (value instanceof AppSubStore) {
        value.resetSync();
      }
    }
  };

  initializeTanstackDatabase = async () => {
    try {
      const spaceAccountId = this.spaceAccounts.myPersonalSpaceAccountId;

      logger.info({
        message: "[initializeTanstackDatabase] Initializing database",
        info: { spaceAccountId },
      });

      await this.db.init({ spaceAccountId });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.error({
        message: "[initializeTanstackDatabase] Failed to initialize database",
        info: { err: objectModule.safeErrorAsJson(err) },
      });

      throw err;
    }
  };

  addDbListeners() {
    this.memDb.on("versionchange", () => {
      logger.info({
        message: "[MemDB] version changed",
      });

      // TODO: show modal dialog with "version changed, please refresh" message
      // const isDesktop = clientEnvModule.isDesktop();
    });
  }

  // When tab freezes we want to close db connection and allow other tabs (if any) to continue operating
  // If we don't, the app can get into the state it's no longer able to converse with indexedDb
  // Based on this recommendation: https://developer.chrome.com/docs/web-platform/page-lifecycle-api

  // When tab is hidden, we want to pause the Q
  // When tab exits hidden state, we want to resume the Q (assuming tab is not frozen)
  private addPageLifecycleReactions = () => {
    reaction(
      () => this.tabLifecycleManager.state,
      (tabState, previousTabState) => {
        if (tabState === TabLifecycleState.Frozen) {
          return this.onFreeze();
        }

        if (previousTabState === TabLifecycleState.Frozen) {
          return this.onUnfreeze();
        }

        if (tabState === TabLifecycleState.Hidden) {
          return this.onHide();
        }

        if (previousTabState === TabLifecycleState.Hidden) {
          return this.onShow();
        }
      }
    );
  };

  // when tab is hidden we want to pause the queue
  private onHide = () => {
    this.tabDetector.hasOtherOpenTabs().then(hasOtherTabs => {
      if (!hasOtherTabs) {
        logger.debug({
          message: "[MemDB][Sync] No other tabs detected, skip pausing",
          info: {
            tabState: this.tabLifecycleManager.state,
            queueState: this.sync.actionQueue.processingState,
            tabId: this.tabDetector.tabId,
          },
        });

        return;
      }

      if (this.tabLifecycleManager.state !== TabLifecycleState.Hidden) {
        return;
      }

      logger.info({
        message: "[MemDB][Sync] Pausing queue",
        info: {
          tabState: this.tabLifecycleManager.state,
          queueState: this.sync.actionQueue.processingState,
          tabId: this.tabDetector.tabId,
        },
      });

      this.sync.actionQueue.pause();
    });
  };

  // when tab is shown we want to resume the queue
  private onShow = () => {
    logger.info({
      message: "[MemDB][Sync] Resuming queue",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    this.sync.actionQueue.resume();
  };

  // when tab freezes we want to pause the queue and close the db connection
  private onFreeze = () => {
    logger.info({
      message: "[MemDB][Sync] Freezing app",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    this.sync.actionQueue.pause();
    this.memDb.close();
  };

  // when tab unfreeze we want to reopen db and potentially resume the queue
  private onUnfreeze = () => {
    logger.info({
      message: "[MemDB][Sync] Unfreezing app",
      info: {
        tabState: this.tabLifecycleManager.state,
        queueState: this.sync.actionQueue.processingState,
        tabId: this.tabDetector.tabId,
      },
    });

    if (this.memDb.isOpen()) {
      this.resumeAndPrefillQueueIfTabNotHidden();
      return;
    }

    this.memDb.open().then(() => {
      // there seems to be a bug, when DB is already open,
      // it doesn't fulfill this promise hence check above
      this.resumeAndPrefillQueueIfTabNotHidden();
    });
  };

  // if we are not hidden, we want to resume the queue and prefill it with latest from disk
  private resumeAndPrefillQueueIfTabNotHidden = () => {
    // grace period
    setTimeout(() => {
      if (this.tabLifecycleManager.state === TabLifecycleState.Hidden) {
        logger.info({
          message: "[MemDB][Sync] Tab is hidden, skipping resuming and prefilling queue",
        });
        return;
      }

      logger.info({
        message: "[MemDB][Sync] Tab is not hidden, proceeding resume and prefill queue",
        info: {
          tabState: this.tabLifecycleManager.state,
          queueState: this.sync.actionQueue.processingState,
          tabId: this.tabDetector.tabId,
        },
      });

      this.sync.actionQueue.updateFromLocal();
      this.sync.actionQueue.resume();
    }, 2000);
  };
}
